/**
 * Industry type — the single, platform-wide industry vocabulary (ENH-008 unifies
 * the former provider `IndustryType` and marketplace `MarketplaceIndustryType`
 * into one list). It is used in three places:
 *
 *   1. `tenants.industry_type`               — the provider's own industry (single,
 *                                               immutable, set once at provisioning).
 *   2. `catalogue_services.marketplace_*`    — which industries a published service
 *                                               targets in the marketplace (multi).
 *   3. `platform_users.industry_types`       — a self-service buyer's industries (multi).
 *
 * STRUCTURE: the vocabulary is organised as generic "parent" verticals (e.g.
 * `software_it_services`, `consumer_goods`, `energy`) each with more specific
 * children (e.g. `water_purifier_servicing`, `solar_renewable_equipment_servicing`).
 * A parent doubles as the catch-all for a Provider/Client that spans children or
 * doesn't fit one exactly.
 *
 * VOCABULARY vs PROVISIONABLE SUBSET: every code below is a valid value for the
 * two multi-value (marketplace / platform-user) fields. Only the subset that has a
 * committed CLI catalogue seed pack can be a *provider* industry — that gate lives
 * in the CLI (`availableIndustryPacks()` / `PACK_FILES`) and in the
 * `tenants_industry_type_check` DB CHECK, NOT here. Adding a provider-provisionable
 * industry therefore means: add the code here, add its seed pack + PACK_FILES entry,
 * and widen the tenants CHECK via a migration. Adding a marketplace-only industry
 * means: add the code here only (the array columns are app-validated, no migration).
 */
export const INDUSTRY_CODES = [
  // ── Software / IT — generic parent + specific children ──────────────────────
  "software_it_services",
  "software_consulting",
  "software_product",
  "digital_marketing_services",

  // ── Consumer goods — generic parent + specific children ─────────────────────
  "consumer_goods",
  "water_purifier_servicing",
  "ac_air_cooler_servicing",
  "kitchen_appliance_servicing",
  "home_electronics_servicing",

  // ── Vehicle-related — split by business model ───────────────────────────────
  "vehicle_maintenance_repair",
  "automotive_component_manufacturing",

  // ── Manufacturing — split by vertical ───────────────────────────────────────
  "industrial_equipment_manufacturing",

  // ── Healthcare — generic parent + AMC-specific child ────────────────────────
  "healthcare",
  "medical_equipment_servicing",

  // ── Financial services ──────────────────────────────────────────────────────
  "financial_services",

  // ── Retail / eCommerce ──────────────────────────────────────────────────────
  "retail_ecommerce",

  // ── Logistics ───────────────────────────────────────────────────────────────
  "logistics",

  // ── Education, hospitality, government, non-profit, media ───────────────────
  "education",
  "hospitality",
  "government",
  "non_profit",
  "media",

  // ── Professional services — generic parent + specific child ─────────────────
  "professional_services",
  "tax_consulting",

  // ── Energy — generic parent + AMC-specific child ────────────────────────────
  "energy",
  "solar_renewable_equipment_servicing",

  // ── Real estate — generic parent + AMC-specific child ───────────────────────
  "real_estate",
  "facility_management_servicing",

  // ── Catch-all ────────────────────────────────────────────────────────────────
  "other",
] as const;

/** A single industry code from the platform-wide vocabulary. */
export type IndustryType = (typeof INDUSTRY_CODES)[number];

/** Display metadata for each industry. `code` is the persisted value. */
export interface IndustryMeta {
  code: IndustryType;
  displayName: string;
  description: string;
}

export const INDUSTRIES: readonly IndustryMeta[] = [
  {
    code: "software_it_services",
    displayName: "Software / IT Services (General)",
    description:
      "IT service companies, software/product firms, technology consultancies, and MSPs, or a Provider not fitting either specific child below.",
  },
  {
    code: "software_consulting",
    displayName: "Software / IT Consulting",
    description:
      "IT/software consultancies and MSPs delivering outsourced application management, support, and enhancement work — the classic AMSaaS Provider business model.",
  },
  {
    code: "software_product",
    displayName: "Software Product Company",
    description:
      "Companies building and selling their own software product (SaaS or otherwise) — often an AMSaaS Client buying support for their own infrastructure.",
  },
  {
    code: "digital_marketing_services",
    displayName: "Digital Marketing Services",
    description:
      "Digital marketing, advertising, and creative agencies — an AMSaaS client industry with heavy martech/analytics tooling needs.",
  },
  {
    code: "consumer_goods",
    displayName: "Consumer Goods (General)",
    description:
      "Generic/catch-all appliance servicing, or a Provider spanning multiple appliance categories without fitting one specific child below.",
  },
  {
    code: "water_purifier_servicing",
    displayName: "Water Purifier Servicing",
    description:
      "AMC servicing for RO/UV water purifiers — preventive visits, filter/membrane replacement, breakdown repair.",
  },
  {
    code: "ac_air_cooler_servicing",
    displayName: "AC & Air Cooler Servicing",
    description:
      "AMC servicing for air conditioners and air coolers — preventive maintenance, gas/refrigerant service, seasonal cooler servicing.",
  },
  {
    code: "kitchen_appliance_servicing",
    displayName: "Kitchen Appliance Servicing",
    description:
      "AMC servicing for chimneys, microwaves, dishwashers, and similar kitchen appliances.",
  },
  {
    code: "home_electronics_servicing",
    displayName: "Home Electronics Servicing",
    description:
      "AMC servicing for TVs, home theatre systems, and similar consumer electronics.",
  },
  {
    code: "vehicle_maintenance_repair",
    displayName: "Vehicle Maintenance & Repair",
    description:
      "AMCaaS-style servicing — repair shops, fleet servicing, workshops maintaining vehicles already in service.",
  },
  {
    code: "automotive_component_manufacturing",
    displayName: "Automotive Component Manufacturing",
    description:
      "B2B Tier-1/Tier-2 automotive component manufacturers supplying OEMs as production input (e.g. chains, sprockets, fine-blanked parts).",
  },
  {
    code: "industrial_equipment_manufacturing",
    displayName: "Industrial Equipment Manufacturing",
    description:
      "Machinery/equipment makers who sell maintenance agreements for the equipment they manufacture.",
  },
  {
    code: "healthcare",
    displayName: "Healthcare (General)",
    description:
      "Hospitals/clinics as a generic AMS buyer, or a Provider not fitting the medical equipment child below.",
  },
  {
    code: "medical_equipment_servicing",
    displayName: "Medical Equipment Servicing",
    description:
      "AMC servicing for diagnostic/medical equipment — MRI, CT, X-ray, lab instruments.",
  },
  {
    code: "financial_services",
    displayName: "Financial Services",
    description:
      "Banks, insurers, and financial institutions — an AMSaaS client industry buying application support for compliance-heavy systems.",
  },
  {
    code: "retail_ecommerce",
    displayName: "Retail / eCommerce",
    description:
      "Retail and eCommerce businesses — an AMSaaS client industry needing scalable application support for seasonal traffic spikes.",
  },
  {
    code: "logistics",
    displayName: "Logistics",
    description:
      "Logistics and supply chain businesses — an AMSaaS client industry with ERP/CRM-heavy operational continuity needs.",
  },
  {
    code: "education",
    displayName: "Education",
    description:
      "Schools, universities, and edtech-adjacent institutions — an AMSaaS client industry.",
  },
  {
    code: "hospitality",
    displayName: "Hospitality",
    description:
      "Hotels, restaurants, and hospitality businesses — an AMSaaS client industry.",
  },
  {
    code: "government",
    displayName: "Government",
    description:
      "Government departments and public-sector bodies — an AMSaaS client industry.",
  },
  {
    code: "non_profit",
    displayName: "Non-Profit",
    description:
      "Non-profit and NGO organisations — an AMSaaS client industry.",
  },
  {
    code: "media",
    displayName: "Media",
    description: "Media and publishing businesses — an AMSaaS client industry.",
  },
  {
    code: "professional_services",
    displayName: "Professional Services (General)",
    description:
      "Law firms, general consultancies, and similar professional service firms, or a Client not fitting the tax consulting child below.",
  },
  {
    code: "tax_consulting",
    displayName: "Tax Consulting",
    description:
      "Tax advisory, CA/accounting, and compliance consulting firms — an AMSaaS client industry with regulatory/filing-deadline-driven software needs.",
  },
  {
    code: "energy",
    displayName: "Energy (General)",
    description:
      "Oil & gas and general energy-sector businesses as an AMS buyer, or a Provider not fitting the solar/renewable child below.",
  },
  {
    code: "solar_renewable_equipment_servicing",
    displayName: "Solar & Renewable Equipment Servicing",
    description:
      "AMC servicing for rooftop/commercial solar installations — panel cleaning, inverter servicing, performance monitoring.",
  },
  {
    code: "real_estate",
    displayName: "Real Estate (General)",
    description:
      "Property management/leasing as an AMS buyer, or a Provider not fitting the facility management child below.",
  },
  {
    code: "facility_management_servicing",
    displayName: "Facility Management Servicing",
    description:
      "AMC servicing for building systems — lifts/elevators, DG sets/generators, building HVAC.",
  },
  {
    code: "other",
    displayName: "Other",
    description: "Catch-all for anything not covered above.",
  },
];

/** The default industry — the neutral catch-all (no assumption about the Provider's vertical). */
export const DEFAULT_INDUSTRY: IndustryType = "other";

/** Lookup display metadata for an industry code (falls back to the raw code). */
export function getIndustryMeta(code: IndustryType): IndustryMeta {
  return (
    INDUSTRIES.find((i) => i.code === code) ?? {
      code,
      displayName: code,
      description: "",
    }
  );
}
